import type { MayBe } from 'modules/global/typing/common/common';
import { FeaturesEnum } from 'modules/global/typing/enums/features.enum';
import { IntegrationTypeEnum } from 'modules/global/typing/enums/integrations.enum';
import { TAG_FEATURES_BY_MODE } from 'modules/tags/constants';

import { EXTENSIONS_ENUM } from './ExtensionConstants';
import { FEATURES as FEATURES_ENUM } from './FeatureConstants';
import { GOLD, PLATINUM } from './Global';
import { INTEGRATIONS_DATA_NAME } from './IntegrationConstants';
import { DEALER_CONFIG_PAGES, GROUP_CONFIG_PAGES, ROUTES } from './RoutesConstants';

import {
  ADMIN,
  AUDITOR,
  SALESMAN,
  TECHNICIAN,
  CHAT_ROLES,
  BACK_OFFICE,
  MANUFACTURER,
  AUDIT_MANAGER,
  SALES_MANAGER,
  MANAGER_ROLES,
  PARTS_MANAGER,
  PARTS_OFFICER,
  RELEASE_MANAGER,
  VO_STOCK_MANAGER,
  LEADERSHIP_ROLES,
  CAMPAIGN_MANAGER,
  CAMPAIGN_OFFICER,
  KEY_DEPOSIT_MANAGER,
  AFTER_SALES_MANAGER,
  SELF_SERVICE_MANAGER,
  SELF_SERVICE_OFFICER,
  NO_CONFIGURATION_ACCESS,
  ROLES_WITH_NOTIFICATIONS,
  ALL_ROLES_WITHOUT_MANUFACTURER
} from './UserConstants';

export const ROLES = 'ROLES';
export const PARAMS = 'PARAMS';
export const FEATURES = 'FEATURES';
export const EXTENSIONS = 'EXTENSIONS';
export const ODITOO_PACK = 'ODITOO_PACK';
export const FLEETBACK_PACK = 'FLEETBACK_PACK';

const LEADERS_AND_MANAGERS = [...LEADERSHIP_ROLES, ...MANAGER_ROLES];
const SELF_SERVICE_ROLES = [...LEADERSHIP_ROLES, SELF_SERVICE_MANAGER];

type RightKey = typeof ROLES | typeof EXTENSIONS | typeof FEATURES | typeof ODITOO_PACK | typeof FLEETBACK_PACK;

/** What a user, or a dealer, holds for each right. */
export type RightsMatrix = Partial<Record<RightKey, ReadonlyArray<MayBe<string>>>>;

/** What an entity requires: one of the listed values, for every listed right. */
export type RightsRequirement = Partial<Record<RightKey, ReadonlyArray<string>>>;

/**
 * The access rule of an entity: its own requirement, an optional deny-list, an entity it inherits
 * from, and extra requirements per value of a route parameter.
 */
export type RightsAccessRule = RightsRequirement & {
  extends?: string;
  excludes?: RightsRequirement;
  [PARAMS]?: Record<string, Record<string, RightsRequirement>>;
};

export const RIGHTS_ACCESS: Record<string, RightsAccessRule> = {
  // Public routes
  [ROUTES.HOME]: {},
  [ROUTES.PROFILE]: {},
  [ROUTES.ERROR_404]: {},
  [ROUTES.ERROR_GLOBAL]: {},
  // Dev-only playground. Public on purpose: the route itself is absent when IS_PRODUCTION,
  // so there is nothing to restrict where it exists.
  [ROUTES.PLAYGROUND]: {},
  // Private routes
  [ROUTES.PAYMENT]: {
    [ROLES]: LEADERS_AND_MANAGERS,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.PARTS, EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.AFTERSALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, BACK_OFFICE, TECHNICIAN, SELF_SERVICE_MANAGER, SELF_SERVICE_OFFICER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.BODYWORKS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, BACK_OFFICE, TECHNICIAN],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES],
    [FEATURES]: [FEATURES_ENUM.BODYWORK_APPOINTMENT]
  },
  [ROUTES.REMINDERS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, BACK_OFFICE, TECHNICIAN],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES],
    [FEATURES]: [FEATURES_ENUM.REMINDER]
  },
  [ROUTES.SELF_SERVICE_KANBAN]: {
    [ROLES]: [...SELF_SERVICE_ROLES, SELF_SERVICE_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.SELF_SERVICE_MOBILITY]: {
    [ROLES]: [...SELF_SERVICE_ROLES, SELF_SERVICE_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE],
    [FEATURES]: [FEATURES_ENUM.MOBILITY]
  },
  [ROUTES.FOLLOW_UP]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, BACK_OFFICE, TECHNICIAN, SALES_MANAGER, SALESMAN, PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES, EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.PARTS]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.PARTS_DETAILED]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.PARTS_REMINDER]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS],
    [FEATURES]: [FEATURES_ENUM.PARTS_REMINDER]
  },
  [ROUTES.PARTS_REMINDER_DETAILED]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS],
    [FEATURES]: [FEATURES_ENUM.PARTS_REMINDER]
  },
  [ROUTES.AUDIT_MODELS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AUDIT_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AUDIT]
  },
  [ROUTES.AUDITS]: {
    [ROLES]: [...LEADERSHIP_ROLES, BACK_OFFICE, AUDIT_MANAGER, AUDITOR],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AUDIT]
  },
  [ROUTES.AUDITS_DETAILED]: {
    [ROLES]: [...LEADERS_AND_MANAGERS, BACK_OFFICE, AUDITOR],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AUDIT]
  },
  [ROUTES.CUSTOMERS]: {
    [ROLES]: [...LEADERS_AND_MANAGERS, BACK_OFFICE, CAMPAIGN_OFFICER]
  },
  [ROUTES.STATS_PARTS]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.STATS_SALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, SALESMAN, BACK_OFFICE, VO_STOCK_MANAGER],
    [FLEETBACK_PACK]: [GOLD, PLATINUM],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.STATS_PARTS_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER, PARTS_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.STATS_SALES_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, SALESMAN, BACK_OFFICE, VO_STOCK_MANAGER],
    [FLEETBACK_PACK]: [GOLD, PLATINUM],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.STATS_AFTER_SALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, TECHNICIAN, BACK_OFFICE],
    [FLEETBACK_PACK]: [GOLD, PLATINUM],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [ROUTES.SELF_SERVICE_STATS_DASHBOARD]: {
    [ROLES]: [...SELF_SERVICE_ROLES, SELF_SERVICE_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.SELF_SERVICE_STATS_DETAILS]: {
    [ROLES]: [...SELF_SERVICE_ROLES, SELF_SERVICE_OFFICER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.STATS_AFTER_SALES_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, TECHNICIAN, BACK_OFFICE],
    [FLEETBACK_PACK]: [GOLD, PLATINUM],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [ROUTES.MANUFACTURER_STATS_PARTS]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.MANUFACTURER_STATS_SALES]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.MANUFACTURER_STATS_PARTS_DETAILS]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.MANUFACTURER_STATS_SALES_DETAILS]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.MANUFACTURER_STATS_AFTER_SALES]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [ROUTES.MANUFACTURER_STATS_AFTER_SALES_DETAILS]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [ROUTES.MANUFACTURER_STATS_SELF_SERVICE]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.MANUFACTURER_STATS_SELF_SERVICE_DETAILS]: {
    [ROLES]: [MANUFACTURER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.STATS_AUDITS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AUDIT_MANAGER],
    [ODITOO_PACK]: [GOLD, PLATINUM],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AUDIT]
  },
  [ROUTES.SALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, BACK_OFFICE, SALESMAN, VO_STOCK_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.CONFIGURATOR_OFFERS]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, BACK_OFFICE, SALESMAN],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES],
    [FEATURES]: [FEATURES_ENUM.CAR_CONFIGURATOR]
  },
  [ROUTES.CONFIGURATOR_MODELS]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES],
    [FEATURES]: [FEATURES_ENUM.CAR_CONFIGURATOR]
  },
  [ROUTES.KEY_DROP_OFF_LOCKER_DETAILED]: {
    [ROLES]: [...LEADERSHIP_ROLES, KEY_DEPOSIT_MANAGER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.APPOINTMENT],
    [FEATURES]: [FEATURES_ENUM.LOCKER_BOOKING]
  },
  [ROUTES.KEY_DROP_OFF_APPOINTMENTS]: {
    [ROLES]: [...LEADERSHIP_ROLES, KEY_DEPOSIT_MANAGER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.APPOINTMENT]
  },
  [ROUTES.KEY_DROP_OFF_LOCKER_PLANNING]: {
    [ROLES]: [...LEADERSHIP_ROLES, KEY_DEPOSIT_MANAGER, BACK_OFFICE],
    [EXTENSIONS]: [EXTENSIONS_ENUM.APPOINTMENT],
    [FEATURES]: [FEATURES_ENUM.LOCKER_BOOKING]
  },
  [ROUTES.GROUP_CONFIGURATION]: {
    [ROLES]: LEADERSHIP_ROLES
  },
  [ROUTES.DEALER_CONFIGURATION]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.PERSONAL_CONFIGURATION]: {
    [ROLES]: ALL_ROLES_WITHOUT_MANUFACTURER
  },
  [ROUTES.DEALER_MANAGEMENT]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.CHECKLISTS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [ROUTES.OFFERS_DASHBOARD]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.USER_GROUPS]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.USERS]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.DEVICES]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.DEALERS]: {
    [ROLES]: [ADMIN]
  },
  [ROUTES.CHAT]: {
    [ROLES]: CHAT_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.CHAT]
  },
  [ROUTES.GUIDELINES_DASHBOARD]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.GUIDELINE_DETAILED]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.PREDEFINED_MESSAGES_DASHBOARD]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.KIOSKS]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.KIOSKS_DETAILS]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.DEALER_CONFIG_SCREENSAVERS]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_CONFIG_SCREENSAVERS]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.DEALER_CONFIG_SCREENSAVER_DETAILS]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_CONFIG_SCREENSAVER_DETAILS]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GATE_MANAGEMENT_DASHBOARD]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GATE_MANAGEMENT_DETAILS]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.CAMERAS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, PARTS_MANAGER, SELF_SERVICE_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.CAMERA]
  },
  [ROUTES.WHITELIST]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [FEATURES]: [FEATURES_ENUM.WHITE_LISTED_PLATES]
  },
  [ROUTES.CAMERAS_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, PARTS_MANAGER, SELF_SERVICE_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.CAMERA]
  },
  [ROUTES.HARDWARES]: {
    [ROLES]: [ADMIN],
    [EXTENSIONS]: [EXTENSIONS_ENUM.CAMERA]
  },
  [ROUTES.DEALER_EXTRA_DASHBOARD]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [FEATURES]: [FEATURES_ENUM.SELF_SERVICE_EXTRAS],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.PAYERS]: {
    [ROLES]: LEADERS_AND_MANAGERS,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES, EXTENSIONS_ENUM.PARTS],
    [FEATURES]: [FEATURES_ENUM.MULTIPLE_PAYERS]
  },
  [ROUTES.PAYERS_DETAILS]: {
    [ROLES]: LEADERS_AND_MANAGERS,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES, EXTENSIONS_ENUM.PARTS],
    [FEATURES]: [FEATURES_ENUM.MULTIPLE_PAYERS]
  },
  [ROUTES.DEALER_QUESTION_MODEL_DASHBOARD]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [FEATURES]: [FEATURES_ENUM.SELF_SERVICE_QUESTIONS],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.DEALER_VEHICLE_CHECK_MODEL_DASHBOARD]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_CONFIG_PAGE]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_EXTRA_DASHBOARD]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_QUESTION_MODEL_DASHBOARD]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_VEHICLE_CHECK_MODEL_DASHBOARD]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_PAYERS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES, EXTENSIONS_ENUM.PARTS],
    [FEATURES]: [FEATURES_ENUM.MULTIPLE_PAYERS]
  },
  [ROUTES.GROUP_PAYERS_DETAILS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES, EXTENSIONS_ENUM.PARTS],
    [FEATURES]: [FEATURES_ENUM.MULTIPLE_PAYERS]
  },
  [ROUTES.DEALER_EXTRA_DETAILED]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [FEATURES]: [FEATURES_ENUM.SELF_SERVICE_EXTRAS],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_EXTRA_DETAILED]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_QUESTION_MODEL_DETAILED]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.GROUP_VEHICLE_CHECK_MODEL_DETAILED]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.PREDEFINED_MESSAGES_DETAILED]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.QUESTION_MODEL_DETAILED]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [FEATURES]: [FEATURES_ENUM.SELF_SERVICE_QUESTIONS],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.VEHICLE_CHECK_MODEL_DETAILED]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [ROUTES.SAFE_CONFIG]: {
    [ROLES]: [...LEADERSHIP_ROLES, KEY_DEPOSIT_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.APPOINTMENT],
    [FEATURES]: [FEATURES_ENUM.LOCKER_BOOKING]
  },
  [ROUTES.GROUP_CHECKLISTS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [ROUTES.GROUP_CHECKLISTS_DETAILED]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [ROUTES.GROUP_TEMPLATES]: {
    [ROLES]: LEADERSHIP_ROLES
  },
  [ROUTES.GROUP_GUIDELINES]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.GROUP_GUIDELINE_DETAILED]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.GROUP_OFFERS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.GROUP_OFFERS_DETAILED]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [ROUTES.GROUP_DEVICES]: {
    [ROLES]: LEADERSHIP_ROLES
  },
  [ROUTES.GROUP_PUBLIC_TOKENS_DASHBOARD]: {
    [ROLES]: [ADMIN]
  },
  [ROUTES.DEALER_PUBLIC_TOKENS_DASHBOARD]: {
    [ROLES]: [ADMIN]
  },
  [ROUTES.GROUP_SMS_ACCOUNTS_DASHBOARD]: {
    [ROLES]: [ADMIN]
  },
  [ROUTES.DEALER_SMS_ACCOUNTS_DASHBOARD]: {
    [ROLES]: [ADMIN]
  },
  [ROUTES.DEALER_CCM]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.GROUP_CCM]: {
    [ROLES]: LEADERSHIP_ROLES
  },
  [ROUTES.USER_NOTIFICATIONS_SETTINGS]: {
    [ROLES]: ROLES_WITH_NOTIFICATIONS
  },
  [ROUTES.USER_NOTIFICATIONS]: {
    [ROLES]: ROLES_WITH_NOTIFICATIONS
  },
  [ROUTES.STEPS_DASHBOARD]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, AFTER_SALES_MANAGER, PARTS_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.TRACK]
  },
  [ROUTES.STEPS_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, AFTER_SALES_MANAGER, PARTS_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.TRACK]
  },
  [ROUTES.LEADS]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, SALESMAN, BACK_OFFICE, VO_STOCK_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES],
    [FEATURES]: [FEATURES_ENUM.LEAD]
  },
  [ROUTES.BILLING]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [ROUTES.MESSAGE_BILLING]: {
    extends: ROUTES.BILLING
  },
  [ROUTES.CLIPPING_BILLING]: {
    extends: ROUTES.BILLING,
    [FEATURES]: [FEATURES_ENUM.CLIPPING]
  },
  [ROUTES.AUTOMATED_DOCUMENT_BILLING]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, SELF_SERVICE_MANAGER],
    [FEATURES]: [FeaturesEnum.AUTOMATED_DOCUMENT_VALIDATION]
  },
  [ROUTES.PHONE_AGENT_BILLING]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, SELF_SERVICE_MANAGER],
    [FEATURES]: [FeaturesEnum.PHONE_AGENT]
  },
  [ROUTES.MEDIA_LIBRARY_DEALER]: {
    // eslint-disable-next-line max-len
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, AFTER_SALES_MANAGER, AUDIT_MANAGER, KEY_DEPOSIT_MANAGER, VO_STOCK_MANAGER, PARTS_MANAGER, CAMPAIGN_MANAGER, SELF_SERVICE_MANAGER]
  },
  [ROUTES.MEDIA_LIBRARY_GROUP]: {
    [ROLES]: LEADERSHIP_ROLES
  },
  [ROUTES.MEDIA_LIBRARY_PERSONAL]: {
    [ROLES]: ALL_ROLES_WITHOUT_MANUFACTURER
  },
  /** Campaigns */
  [ROUTES.CAMPAIGNS_DASHBOARD]: {
    [ROLES]: [...LEADERSHIP_ROLES, CAMPAIGN_MANAGER, CAMPAIGN_OFFICER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.CAMPAIGN]
  },
  [ROUTES.CAMPAIGNS_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, CAMPAIGN_MANAGER, CAMPAIGN_OFFICER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.CAMPAIGN]
  },
  [ROUTES.CONTACT_LISTS_DASHBOARD]: {
    [ROLES]: [...LEADERSHIP_ROLES, CAMPAIGN_MANAGER, CAMPAIGN_OFFICER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.CAMPAIGN]
  },
  [ROUTES.CONTACT_LISTS_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, CAMPAIGN_MANAGER, CAMPAIGN_OFFICER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.CAMPAIGN]
  },
  /** Release Notes */
  [ROUTES.RELEASES_DASHBOARD]: {
    [ROLES]: [RELEASE_MANAGER]
  },
  [ROUTES.RELEASES_DETAILS]: {
    [ROLES]: [RELEASE_MANAGER]
  },
  /** Parts offers */
  [ROUTES.GROUP_PARTS_OFFERS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.GROUP_PARTS_OFFERS_DETAILS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.DEALER_PARTS_OFFERS]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.PARTS_OFFERS_DETAILS]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [ROUTES.DEALER_CONFIG_PAGE]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [DEALER_CONFIG_PAGES.GLOBAL]: {
    [ROLES]: ALL_ROLES_WITHOUT_MANUFACTURER,
    excludes: {
      [ROLES]: [NO_CONFIGURATION_ACCESS]
    }
  },
  [DEALER_CONFIG_PAGES.CHAT]: {
    extends: DEALER_CONFIG_PAGES.GLOBAL,
    [ROLES]: LEADERS_AND_MANAGERS,
    [EXTENSIONS]: [EXTENSIONS_ENUM.CHAT]
  },
  [DEALER_CONFIG_PAGES.PARTS]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [DEALER_CONFIG_PAGES.PARTS_STEPS]: {
    [ROLES]: [ADMIN],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [DEALER_CONFIG_PAGES.PARTS_CLIPPING]: {
    [ROLES]: [ADMIN],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [DEALER_CONFIG_PAGES.ADMINISTRATION]: {
    [ROLES]: [ADMIN]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_AFTERSALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_SELFSERVICE]: {
    [ROLES]: SELF_SERVICE_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_PARTS]: {
    [ROLES]: [...LEADERSHIP_ROLES, PARTS_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_SALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_CHATBOT]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_ONLINEPAYMENT]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER, AFTER_SALES_MANAGER, PARTS_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SALES, EXTENSIONS_ENUM.PARTS]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_AUDIT]: {
    [ROLES]: [...LEADERSHIP_ROLES, AUDIT_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AUDIT]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_KEYDEPOSIT]: {
    [ROLES]: [...LEADERSHIP_ROLES, KEY_DEPOSIT_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.APPOINTMENT]
  },
  [DEALER_CONFIG_PAGES.NOTIFICATION_CHAT]: {
    [ROLES]: LEADERS_AND_MANAGERS,
    [EXTENSIONS]: [EXTENSIONS_ENUM.CHAT]
  },
  [DEALER_CONFIG_PAGES.AUDIT]: {
    [ROLES]: [...LEADERSHIP_ROLES, AUDIT_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AUDIT]
  },
  [DEALER_CONFIG_PAGES.AFTERSALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, AFTER_SALES_MANAGER, SELF_SERVICE_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [DEALER_CONFIG_PAGES.SALES]: {
    [ROLES]: [...LEADERSHIP_ROLES, SALES_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [DEALER_CONFIG_PAGES.SALES_CLIPPING]: {
    [ROLES]: [ADMIN],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [DEALER_CONFIG_PAGES.KEYDEPOSIT]: {
    [ROLES]: [...LEADERSHIP_ROLES, KEY_DEPOSIT_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.APPOINTMENT]
  },
  [DEALER_CONFIG_PAGES.CUSTOMISATION_BRANDING]: {
    [ROLES]: [ADMIN]
  },
  [DEALER_CONFIG_PAGES.SELFSERVICE]: {
    [ROLES]: [...LEADERSHIP_ROLES, SELF_SERVICE_MANAGER],
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [DEALER_CONFIG_PAGES.STATISTICS]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [DEALER_CONFIG_PAGES.CUSTOMISATION_TEMPLATES]: {
    [ROLES]: LEADERS_AND_MANAGERS.filter(role => role !== SELF_SERVICE_MANAGER)
  },
  [DEALER_CONFIG_PAGES.INTEGRATION]: {
    [ROLES]: LEADERS_AND_MANAGERS
  },
  [DEALER_CONFIG_PAGES.INTEGRATION_ACTIVATION]: {
    [ROLES]: [ADMIN]
  },
  [DEALER_CONFIG_PAGES.INTEGRATION_SMS_PROVIDER]: {
    [ROLES]: [ADMIN]
  },
  [GROUP_CONFIG_PAGES.AFTERSALES]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [GROUP_CONFIG_PAGES.SALES]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SALES]
  },
  [GROUP_CONFIG_PAGES.PARTS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS]
  },
  [GROUP_CONFIG_PAGES.SELFSERVICE]: {
    [ROLES]: LEADERSHIP_ROLES,
    [EXTENSIONS]: [EXTENSIONS_ENUM.SELF_SERVICE]
  },
  [GROUP_CONFIG_PAGES.SALES_TAGS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [FEATURES]: [FEATURES_ENUM.TAGS_SALES]
  },
  [GROUP_CONFIG_PAGES.AFTERSALES_TAGS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [FEATURES]: [FEATURES_ENUM.TAGS_AFTERSALES]
  },
  [GROUP_CONFIG_PAGES.PARTS_TAGS]: {
    [ROLES]: LEADERSHIP_ROLES,
    [FEATURES]: [FEATURES_ENUM.TAGS_PARTS]
  },
  ...Object.values(IntegrationTypeEnum).reduce((acc, integrationType) => {
    const leaf = INTEGRATIONS_DATA_NAME[integrationType] ?? integrationType;

    return {
      ...acc,
      [`${DEALER_CONFIG_PAGES.INTEGRATION}_${leaf.toUpperCase()}`]: {
        [ROLES]: [ADMIN]
      }
    };
  }, {}),
  [ROUTES.DEALER_TAGS_DASHBOARD]: {
    [ROLES]: LEADERS_AND_MANAGERS,
    [EXTENSIONS]: [EXTENSIONS_ENUM.PARTS, EXTENSIONS_ENUM.SALES, EXTENSIONS_ENUM.AFTER_SALES, EXTENSIONS_ENUM.SELF_SERVICE],
    [PARAMS]: {
      mode: TAG_FEATURES_BY_MODE
    }
  },
  [ROUTES.DEALER_TAGS_DETAILS]: {
    extends: ROUTES.DEALER_TAGS_DASHBOARD
  },
  [ROUTES.GROUP_TAGS_DASHBOARD]: {
    [ROLES]: LEADERSHIP_ROLES,
    extends: ROUTES.DEALER_TAGS_DASHBOARD
  },
  [ROUTES.GROUP_TAGS_DETAILS]: {
    extends: ROUTES.DEALER_TAGS_DETAILS
  }
};
